import { useMutation, useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/Label';
import { XStack, YStack } from '@/components/ui/layout/layout';
import TextArea from '@/components/ui/TextArea';
import { Text } from '@/components/ui/typography';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';

import {
  DayOfWeek,
  DefaultNotificationHour,
  type JournalConfig,
  JournalLanguage,
  JournalNotificationTime,
  JournalNotificationType,
  JournalProgressSectionMode,
  type NotificationTime,
  NotificationTimeOptions,
  type ProgressSectionConfig,
  resetJournalSectionsMutation,
} from '../../journal/journal-queries';
import { JournalSectionsSettings } from '../../journal/journal-settings-sections';
import { SettingsSection } from '../settings-section';

export const JournalSettings = observer(({}: { onClose?: () => void }) => {
  const user = userStore.user;

  const [inputSummaryPrompt, setInputSummaryPrompt] = useState(
    user?.summary_prompt ?? ''
  );

  const handleSummaryPromptChange = useCallback(() => {
    if (user && inputSummaryPrompt !== user?.summary_prompt) {
      userStore.syncUpdate({
        ...user,
        summary_prompt: inputSummaryPrompt,
      });
      trackEvent('JOURNAL_SETTING_PROMPT_UPDATED');
    }
  }, [user, inputSummaryPrompt]);

  useEffect(() => {
    return () => {
      handleSummaryPromptChange();
    };
  }, [handleSummaryPromptChange]);

  const journalConfig: JournalConfig = user?.journal_config || {
    notification_time:
      user?.journal_notification_time || JournalNotificationTime.MORNING,
    notification_hour: DefaultNotificationHour,
    notification_type: JournalNotificationType.TLDR,
    expand_sections: true,
    sections: [],
    language: JournalLanguage.ENGLISH,
    mobile_notifications_enabled: true,
  };

  // Read notification time: prioritize notification_hour (new), fall back to notification_time (legacy)
  const getInitialNotificationTime = (): NotificationTime => {
    if (journalConfig.notification_time === 'none') {
      return 'none';
    }

    if (journalConfig.notification_hour !== undefined) {
      return journalConfig.notification_hour as NotificationTime;
    }

    if (journalConfig.notification_time === 'morning') {
      return 9;
    }

    return DefaultNotificationHour;
  };

  const [notificationTime, setNotificationTime] = useState<NotificationTime>(
    getInitialNotificationTime()
  );
  const [notificationType, setNotificationType] =
    useState<JournalNotificationType>(
      journalConfig.notification_type || JournalNotificationType.TLDR
    );
  const [expandSections, setExpandSections] = useState<boolean>(
    journalConfig.expand_sections
  );
  const [language, setLanguage] = useState<JournalLanguage>(
    journalConfig.language || JournalLanguage.ENGLISH
  );
  const [progressSectionMode, setProgressSectionMode] =
    useState<JournalProgressSectionMode>(
      journalConfig.progress_section?.mode || JournalProgressSectionMode.ALWAYS
    );
  const [progressSectionDayOfWeek, setProgressSectionDayOfWeek] =
    useState<DayOfWeek>(
      journalConfig.progress_section?.day_of_week || DayOfWeek.MONDAY
    );
  const [isJournalCaptureExpanded, setIsJournalCaptureExpanded] =
    useState(false);
  const [mobileNotificationsEnabled, setMobileNotificationsEnabled] =
    useState<boolean>(journalConfig.mobile_notifications_enabled ?? true);

  const handleNotificationTimeChange = (time: NotificationTime) => {
    setNotificationTime(time);
    if (user) {
      const newConfig: JournalConfig = {
        ...journalConfig,
      };

      if (time === 'none') {
        newConfig.notification_time = JournalNotificationTime.NONE;
      } else if (typeof time === 'number') {
        newConfig.notification_hour = time;
        // Why set notification_time to morning?
        // Because `newConfig.notification_time=none` is used to disable email notifications
        // Since the user has now selected a time, we need to enable notifications for them
        // `notification_time` will be removed once all the clients are updated to use the new field
        // post which we won't need this
        newConfig.notification_time = JournalNotificationTime.MORNING;
      }

      userStore.syncUpdate({
        ...user,
        journal_config: newConfig,
      });
    }
  };

  const handleNotificationTypeChange = (type: JournalNotificationType) => {
    setNotificationType(type);
    if (user) {
      const newConfig: JournalConfig = {
        ...journalConfig,
        notification_type: type,
      };

      userStore.syncUpdate({
        ...user,
        journal_config: newConfig,
      });
    }
  };

  const handleExpandSectionsChange = (checked: boolean) => {
    setExpandSections(checked);
    if (user) {
      const newConfig: JournalConfig = {
        ...journalConfig,
        expand_sections: checked,
      };

      userStore.syncUpdate({
        ...user,
        journal_config: newConfig,
      });
    }
  };

  const handleLanguageChange = (lang: JournalLanguage) => {
    setLanguage(lang);
    if (user) {
      const newConfig: JournalConfig = {
        ...journalConfig,
        language: lang,
      };

      userStore.syncUpdate({
        ...user,
        journal_config: newConfig,
      });
    }
  };

  const handleProgressSectionChange = (
    mode: JournalProgressSectionMode,
    dayOfWeek?: DayOfWeek
  ) => {
    setProgressSectionMode(mode);
    if (dayOfWeek !== undefined) {
      setProgressSectionDayOfWeek(dayOfWeek);
    }
    if (user) {
      const progressSection: ProgressSectionConfig = {
        mode,
        ...(mode === JournalProgressSectionMode.WEEKLY && {
          day_of_week: dayOfWeek ?? progressSectionDayOfWeek,
        }),
      };
      const newConfig: JournalConfig = {
        ...journalConfig,
        progress_section: progressSection,
      };

      userStore.syncUpdate({
        ...user,
        journal_config: newConfig,
      });
    }
  };

  const handleMobileNotificationsChange = (enabled: boolean) => {
    setMobileNotificationsEnabled(enabled);
    if (user) {
      const newConfig: JournalConfig = {
        ...journalConfig,
        mobile_notifications_enabled: enabled,
      };

      userStore.syncUpdate({
        ...user,
        journal_config: newConfig,
      });
    }
  };

  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Journal</SettingsSection.Title>
      </SettingsSection.Header>
      <SettingsSection.Group>
        <ExpandSectionsToggle
          expandSections={expandSections}
          onToggle={handleExpandSectionsChange}
        />
        <SettingsSection.Seperator />
        <JournalNotificationSelector
          notificationTime={notificationTime}
          onChange={handleNotificationTimeChange}
        />
        <SettingsSection.Seperator />
        <JournalNotificationTypeSelector
          notificationType={notificationType}
          onChange={handleNotificationTypeChange}
        />
        <SettingsSection.Seperator />
        <JournalLanguageSelector
          language={language}
          onChange={handleLanguageChange}
        />
        <SettingsSection.Seperator />
        <ProgressSectionSelector
          mode={progressSectionMode}
          dayOfWeek={progressSectionDayOfWeek}
          onChange={handleProgressSectionChange}
        />
        <SettingsSection.Seperator />
        <MobileNotificationsToggle
          enabled={mobileNotificationsEnabled}
          onToggle={handleMobileNotificationsChange}
        />
      </SettingsSection.Group>

      <JournalSectionsSettings />

      {/* Only shown if there's data */}
      {inputSummaryPrompt && inputSummaryPrompt.trim() !== '' && (
        <JournalCaptureSection
          prompt={inputSummaryPrompt}
          setPrompt={setInputSummaryPrompt}
          onBlur={handleSummaryPromptChange}
          isExpanded={isJournalCaptureExpanded}
          setIsExpanded={setIsJournalCaptureExpanded}
        />
      )}

      <JournalResetConfiguration />
    </SettingsSection.Root>
  );
});

const ExpandSectionsToggle = ({
  expandSections,
  onToggle,
}: {
  expandSections: boolean;
  onToggle: (checked: boolean) => void;
}) => {
  return (
    <SettingsSection.Item searchId="journal.expand-sections">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Expand Sections by Default
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Enable or disable the expansion of journal sections by default
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SettingsSection.Switch
        checked={expandSections}
        onCheckedChange={onToggle}
      />
    </SettingsSection.Item>
  );
};

const JournalNotificationSelector = ({
  notificationTime,
  onChange,
}: {
  notificationTime: NotificationTime;
  onChange: (time: NotificationTime) => void;
}) => {
  const currentLabel =
    NotificationTimeOptions.find((opt) => opt.value === notificationTime)
      ?.label || '9 AM';

  return (
    <SettingsSection.Item searchId="journal.email-delivery-time">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Email Delivery Time
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Choose the time of day you want to receive your journal notification
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger className="text-foreground">
          {currentLabel}
          <Icons.chevronDown className="size-3 text-foreground" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end" className="w-40 mt-1">
          {NotificationTimeOptions.map((option) => (
            <DropdownMenu.Item
              key={option.value}
              onClick={() => onChange(option.value)}
              className="text-foreground"
            >
              {option.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
};

const JournalNotificationTypeSelector = ({
  notificationType,
  onChange,
}: {
  notificationType: JournalNotificationType;
  onChange: (type: JournalNotificationType) => void;
}) => {
  return (
    <SettingsSection.Item searchId="journal.email-content">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Email Content</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Choose between receiving a full journal or a TLDR summary
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger className="capitalize text-foreground">
          {notificationType}
          <Icons.chevronDown className="size-3 text-foreground" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end" className="w-40 mt-1">
          {Object.values(JournalNotificationType).map((option) => (
            <DropdownMenu.Item
              key={option}
              onClick={() => onChange(option)}
              className="capitalize text-foreground"
            >
              {option}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
};

const JournalLanguageSelector = ({
  language,
  onChange,
}: {
  language: JournalLanguage;
  onChange: (lang: JournalLanguage) => void;
}) => {
  return (
    <SettingsSection.Item searchId="journal.language">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Language</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Choose the language for your journal
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger className="capitalize text-foreground">
          {language}
          <Icons.chevronDown className="size-3 text-foreground" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end" className="w-40 mt-1">
          {Object.values(JournalLanguage).map((option) => (
            <DropdownMenu.Item
              key={option}
              onClick={() => onChange(option)}
              className="capitalize text-foreground"
            >
              {option}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </SettingsSection.Item>
  );
};

const PROGRESS_MODE_LABELS: Record<JournalProgressSectionMode, string> = {
  [JournalProgressSectionMode.ALWAYS]: 'Yes',
  [JournalProgressSectionMode.NEVER]: 'No',
  [JournalProgressSectionMode.WEEKLY]: 'Once per week',
};

const ProgressSectionSelector = ({
  mode,
  dayOfWeek,
  onChange,
}: {
  mode: JournalProgressSectionMode;
  dayOfWeek: DayOfWeek;
  onChange: (mode: JournalProgressSectionMode, dayOfWeek?: DayOfWeek) => void;
}) => {
  return (
    <SettingsSection.Item searchId="journal.progress-section">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Show Past Week's Progress
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Control when to include a progress summary from the past week
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <XStack className="gap-2 items-center">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger className="text-foreground">
            {PROGRESS_MODE_LABELS[mode]}
            <Icons.chevronDown className="size-3 text-foreground" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="end" className="w-40 mt-1">
            {Object.values(JournalProgressSectionMode).map((option) => (
              <DropdownMenu.Item
                key={option}
                onClick={() => onChange(option)}
                className="text-foreground"
              >
                {PROGRESS_MODE_LABELS[option]}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Root>
        {mode === JournalProgressSectionMode.WEEKLY && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger className="capitalize text-foreground">
              {dayOfWeek}
              <Icons.chevronDown className="size-3 text-foreground" />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="end" className="w-40 mt-1">
              {Object.values(DayOfWeek).map((day) => (
                <DropdownMenu.Item
                  key={day}
                  onClick={() => onChange(mode, day)}
                  className="capitalize text-foreground"
                >
                  {day}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        )}
      </XStack>
    </SettingsSection.Item>
  );
};

const MobileNotificationsToggle = ({
  enabled,
  onToggle,
}: {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
}) => {
  return (
    <SettingsSection.Item searchId="journal.mobile-push">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>
          Mobile Push Notifications
        </SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Receive push notifications on your mobile device when your journal is
          ready
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      <SettingsSection.Switch checked={enabled} onCheckedChange={onToggle} />
    </SettingsSection.Item>
  );
};

const JournalCaptureSection = ({
  prompt,
  setPrompt,
  onBlur,
  isExpanded,
  setIsExpanded,
}: {
  prompt: string;
  setPrompt: (value: string) => void;
  onBlur: () => void;
  isExpanded: boolean;
  setIsExpanded: (value: boolean) => void;
}) => {
  return (
    <YStack className="gap-2">
      <XStack
        className="justify-between items-center cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Label htmlFor="custom-prompt" className="text-foreground">
          Journal Custom Prompt
        </Label>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="text-foreground"
        >
          <Text className="text-foreground">
            {isExpanded ? 'Collapse' : 'Expand'}
          </Text>
        </Button>
      </XStack>

      {isExpanded && (
        <TextArea
          id="custom-prompt"
          className="h-64 text-sm resize-none mt-2"
          placeholder="Enter your custom prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onBlur={onBlur}
        />
      )}
    </YStack>
  );
};

const JournalResetConfiguration = () => {
  const [isResetConfirmationVisible, setIsResetConfirmationVisible] =
    useState(false);

  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const { mutate: resetJournal } = useMutation({
    ...resetJournalSectionsMutation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      window.history.back();
    },
  });

  const handleShowResetConfirmation = () => {
    setIsResetConfirmationVisible(true);

    setTimeout(() => {
      if (confirmButtonRef.current) {
        confirmButtonRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }
    }, 100);
  };

  const handleCancelResetConfirmation = () => {
    setIsResetConfirmationVisible(false);
  };

  const handleResetJournalSections = () => {
    setIsResetConfirmationVisible(false);
    resetJournal();
  };

  return (
    <SettingsSection.Item
      direction="vertical"
      searchId="journal.reset-configuration"
      className="gap-2"
    >
      <XStack className="justify-between items-center">
        <YStack className="justify-between items-start text-sm gap-1">
          <Text className="text-foreground">Reset Journal Configuration</Text>
        </YStack>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleShowResetConfirmation}
          disabled={isResetConfirmationVisible}
        >
          Reset
        </Button>
      </XStack>

      {isResetConfirmationVisible && (
        <ResetJournalConfirmation
          onCancel={handleCancelResetConfirmation}
          onConfirm={handleResetJournalSections}
          confirmButtonRef={confirmButtonRef}
        />
      )}
    </SettingsSection.Item>
  );
};

const ResetJournalConfirmation = ({
  onCancel,
  onConfirm,
  confirmButtonRef,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  confirmButtonRef: React.RefObject<HTMLButtonElement | null>;
}) => {
  return (
    <YStack className="bg-surface-hover rounded-md p-3 mt-1 border border-border">
      <YStack className="gap-2">
        <Text className="text-destructive">
          Are you sure you want to reset your journal configuration?
        </Text>
        <XStack className="justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            ref={confirmButtonRef}
          >
            Confirm
          </Button>
        </XStack>
      </YStack>
    </YStack>
  );
};
